import { AxiosError } from 'axios';
import { QueryKey, useInfiniteQuery, UseInfiniteQueryOptions, useMutation, useQueries, useQuery } from 'react-query';
import { talentDao } from 'shared/dao/talentCalendarDao';
import { ICommonResponsePayload } from 'shared/interfaces/ICommon';
import {
  IFilterVariables,
  IMediaTagsResponsePayload,
  IProfileResponsePayload,
} from 'shared/interfaces/IProfileCalendar';
import {
  IGuardianAttachPayload,
  ISearchTalentResponsePayload,
  ISenderInfoResponsePayload,
  ISpecificTalentResponsePayload,
  ITalentAgencyResponsePayload,
  ITalentAgencyUpdateRequestPayload,
  ITalentBiographyResponsePayload,
  ITalentCalendarEventAggregatesPayload,
  ITalentCalendarEventAggregatesResponse,
  ITalentCalendarEventPayload,
  ITalentCalendarEventsPayload,
  ITalentCalendarEventsResponse,
  ITalentCreatePayload,
  ITalentCreateResponse,
  ITalentGeneralResponsePayload,
  ITalentInvitePayload,
  ITalentListingResponse,
  ITalentListPayload,
  ITalentListRequestPayload,
  ITalentListResponsePayload,
  ITalentMeResponsePayload,
  ITalentPlaylistPayload,
  ITalentPlaylistResponse,
  ITalentResumeResponsePayload,
  ITalentSkillResponsePayload,
  ITalentStatisticsResponsePayload,
  ITalentUpdatePayload,
  TSearchTalentPayload,
  TTalentTabs,
} from 'shared/interfaces/ITalentCalendar';
import { IAxiosErrorResponse, IErrorResponse } from 'shared/interfaces/utils/IAxios';

const {
  getTalentProfiles: getTalentProfilesDao,
  getResume: getResumeDao,
  getStatistics: getStatisticsDao,
  getMediaTags: getMediaTagsDao,
  searchTalent: searchTalentDao,
  specificTalent: specificTalentDao,
  getSizeCard: getSizeCardDao,
  createTalent: createTalentDao,
  getSkills: getSkillsDao,
  getGeneral: getGeneralDao,
  getMe: getMeDao,
  getBiography: getBiographyDao,
  updateTalent: updateTalentDao,
  updateMe: updateMeDao,
  getSenderDetails: getSenderDetailsDao,
  updateAgencyTalent: updateAgencyTalentDao,
  attachTalentToGuardian: attachTalentToGuardianDao,
  searchTalentListing: searchTalentListingDao,
  getTalentList: getTalentListDao,
  getTotalTalent: getTotalTalentDao,
  sendTalentInvites: sendTalentInvitesDao,
  createTalentPalylist: createTalentPalylistDao,
  specificTalentByEmail: specificTalentByEmailDao,
  claimGhostedProfile: claimGhostedProfileDao,
  specificTalentGeneralAppearance: specificTalentGeneralAppearanceDao,
  specificTalentSizeCard: specificTalentSizeCardDao,
  specificTalentWorksheetSizeCard: specificTalentWorksheetSizeCardDao,
  specificTalentResume: specificTalentResumeDao,
  specificTalentBiography: specificTalentBiographyDao,
  specificTalentSkills: specificTalentSkillsDao,
  specificTalentSocial: specificTalentSocialDao,
  specificTalentProfessions: specificTalentProfessionsDao,
  getTalentPlaylist: getTalentPlaylistDao,
  getTalentCalendarEvents: getTalentCalendarEventsDao,
  createCalendarEvent: createCalendarEventDao,
  updateCalendarEvent: updateCalendarEventDao,
  getCalendarAggregates: getCalendarAggregatesDao,
  deleteCalendarEvent: deleteCalendarEventDao,
} = talentDao();

export const talentService = () => {
  const updateMe = () => {
    return useMutation((payload: ITalentUpdatePayload) => updateMeDao(payload));
  };

  const searchTalent = (searchObject: TSearchTalentPayload) => {
    return useQuery<ISearchTalentResponsePayload, Error>(
      ['talent_search', searchObject],
      () => searchTalentDao(searchObject),
      { enabled: false },
    );
  };

  const specificTalent = (talentId: string) => {
    return useQuery<ISpecificTalentResponsePayload, Error>(['talent', talentId], () => specificTalentDao(talentId));
  };

  const getTalentPlaylist = (parentProfileId: string) => {
    return useQuery<ITalentPlaylistResponse, Error>(['talent_playlist', parentProfileId], () =>
      getTalentPlaylistDao(parentProfileId),
    );
  };

  const specificTalentGeneralAppearance = (talentId: string) => {
    return useQuery<ISpecificTalentResponsePayload, Error>(['talent/general', talentId], () =>
      specificTalentGeneralAppearanceDao(talentId),
    );
  };

  const specificTalentSizeCard = (talentId: string) => {
    return useQuery<ISpecificTalentResponsePayload, Error>(['talent/size_card', talentId], () =>
      specificTalentSizeCardDao(talentId),
    );
  };

  const specificTalentWorksheetSizeCard = (talentId: string) => {
    return useQuery<ISpecificTalentResponsePayload, Error>(['talent/size_card', talentId], () =>
      specificTalentWorksheetSizeCardDao(talentId),
    );
  };

  const specificTalentResume = (talentId: string) => {
    return useQuery<ISpecificTalentResponsePayload, Error>(['talent/resume', talentId], () =>
      specificTalentResumeDao(talentId),
    );
  };

  const specificTalentBiography = (talentId: string) => {
    return useQuery<ISpecificTalentResponsePayload, Error>(['talent/biography', talentId], () =>
      specificTalentBiographyDao(talentId),
    );
  };

  const specificTalentSkills = (talentId: string) => {
    return useQuery<ISpecificTalentResponsePayload, Error>(['talent/skills', talentId], () =>
      specificTalentSkillsDao(talentId),
    );
  };

  const specificTalentSocial = (talentId: string) => {
    return useQuery<ISpecificTalentResponsePayload, Error>(['talent/social', talentId], () =>
      specificTalentSocialDao(talentId),
    );
  };

  const specificTalentByEmail = (email: string) => {
    return useQuery<ISpecificTalentResponsePayload, Error>(['talent', email], () => specificTalentByEmailDao(email));
  };

  const specificTalentProfessions = (talentId: string) => {
    return useQuery<ISpecificTalentResponsePayload, Error>(['talent/professions', talentId], () =>
      specificTalentProfessionsDao(talentId),
    );
  };

  const getSizeCard = (talentId: string) => {
    return useQuery<ISpecificTalentResponsePayload, Error>(['size_card', talentId], () => getSizeCardDao(talentId));
  };

  const updateTalent = () => {
    return useMutation<ITalentAgencyResponsePayload, AxiosError<IErrorResponse>, ITalentUpdatePayload>(
      (payload: ITalentUpdatePayload) => updateTalentDao(payload),
    );
  };

  const getMediaTags = (searchString: string) => {
    return useQuery<IMediaTagsResponsePayload, IAxiosErrorResponse>(['media_tags', searchString], () =>
      getMediaTagsDao(searchString),
    );
  };

  const getTalentProfiles = (variables: IFilterVariables) => {
    return useQuery<IProfileResponsePayload, IAxiosErrorResponse>(['profiles', variables], () =>
      getTalentProfilesDao(variables),
    );
  };

  const getResume = (talent_id: string) => {
    return useQuery<ITalentResumeResponsePayload>(['talents/resume', talent_id], () => getResumeDao(talent_id));
  };

  const getStatistics = () => {
    return useQuery<ITalentStatisticsResponsePayload>(['talents/statistics'], () => getStatisticsDao());
  };

  const createTalent = () => {
    return useMutation<ITalentCreateResponse, AxiosError<IErrorResponse>, ITalentCreatePayload>(
      (payload: ITalentCreatePayload) => createTalentDao(payload),
    );
  };
  const getSkills = () => {
    return useQuery<ITalentSkillResponsePayload>(['talents/skills'], () => getSkillsDao());
  };

  const getGeneral = () => {
    return useQuery<ITalentGeneralResponsePayload>(['talents/general'], () => getGeneralDao());
  };

  const getMe = () => {
    return useQuery<ITalentMeResponsePayload>(['me'], () => getMeDao());
  };

  const getBiography = () => {
    return useQuery<ITalentBiographyResponsePayload>(['talents/biography'], () => getBiographyDao());
  };

  const getSenderDetails = (id: string, type: 'Talent' | 'Company') => {
    return useQuery<ISenderInfoResponsePayload>(['sender_details', id, type], () => getSenderDetailsDao(id, type));
  };

  const updateAgencyTalent = () => {
    return useMutation<
      ITalentAgencyResponsePayload,
      IAxiosErrorResponse,
      { id: string; payload: ITalentAgencyUpdateRequestPayload | ITalentUpdatePayload }
    >(({ id, payload }) => updateAgencyTalentDao(id, payload));
  };

  const claimGhostedProfile = () => {
    return useMutation(
      ({ profileId, medium_id, user_id }: { profileId: string[]; medium_id?: string; user_id?: string }) =>
        claimGhostedProfileDao(profileId, medium_id, user_id),
    );
  };

  const attachTalentToGuardian = () => {
    return useMutation<string, Error, { payload: IGuardianAttachPayload }>(({ payload }) =>
      attachTalentToGuardianDao(payload),
    );
  };

  const searchTalentListing = (
    payload: ITalentListPayload,
    isQS?: boolean,
    customGraphQLResponse?: string,
    options?: Omit<
      UseInfiniteQueryOptions<ITalentListingResponse, IAxiosErrorResponse, ITalentListingResponse, QueryKey>,
      'queryKey' | 'queryFn'
    >,
  ) => {
    return useInfiniteQuery<ITalentListingResponse, IAxiosErrorResponse, ITalentListingResponse>(
      ['talent_listing_data', payload],
      ({ pageParam = '' }) => searchTalentListingDao({ ...payload, after: pageParam }, isQS, customGraphQLResponse),
      {
        getNextPageParam: (lastPage) => {
          return lastPage.data.profiles_connection.pageInfo.hasNextPage
            ? lastPage.data.profiles_connection.pageInfo.endCursor
            : undefined;
        },
        enabled: options?.enabled,
      },
    );
  };

  const getTalentList = (
    payload: ITalentListRequestPayload,
    query?: string,
    options?: Omit<
      UseInfiniteQueryOptions<ITalentListResponsePayload, IAxiosErrorResponse, ITalentListResponsePayload, QueryKey>,
      'queryKey' | 'queryFn'
    >,
  ) => {
    return useInfiniteQuery<ITalentListResponsePayload, IAxiosErrorResponse, ITalentListResponsePayload>(
      ['talent_listing_data', payload],
      ({ pageParam = '' }) => getTalentListDao({ ...payload, after: pageParam }, query || ''),
      {
        getNextPageParam: (lastPage) => {
          return lastPage.data.talents_connection.pageInfo.hasNextPage
            ? lastPage.data.talents_connection.pageInfo.endCursor
            : undefined;
        },
        enabled: options?.enabled,
      },
    );
  };

  const getTotalTalent = (payload: Omit<ITalentListPayload, 'first'>, tab: TTalentTabs) => {
    const defaultPayload = {
      after: '',
      search: '',
      division_ids: [],
      genders: [],
      age_range_from: null,
      age_range_to: null,
      media_tags: [],
      profile_types: [],
      sag_statuses: [],
      countries: [],
      // archived: false,
    };
    return useQueries([
      {
        queryKey: ['new_talent_total', payload, tab],
        queryFn: () =>
          getTotalTalentDao({
            ...(tab === 'new' ? { ...payload } : { ...defaultPayload }),
            after: '',
            unviewed: true,
          }),
      },
      {
        queryKey: ['uncategorized_talent_total', payload, tab],
        queryFn: () =>
          getTotalTalentDao({
            ...(tab === 'uncategorized' ? { ...payload } : { ...defaultPayload }),
            after: '',
            uncategorized: true,
            // my_talents: false,
          }),
      },
      {
        queryKey: ['my_talent_total', payload, tab],
        queryFn: () =>
          getTotalTalentDao({
            ...(tab === 'my_talent' ? { ...payload } : { ...defaultPayload }),
            after: '',
            my_talents: true,
          }),
      },
      {
        queryKey: ['talent_library_total', payload, tab],
        queryFn: () =>
          getTotalTalentDao({
            ...(tab === 'talent_library' ? { ...payload } : { ...defaultPayload }),
            after: '',
          }),
      },
    ]);
  };

  const sendTalentInvites = () => {
    return useMutation<{}, AxiosError<IErrorResponse>, ITalentInvitePayload>((payload: ITalentInvitePayload) =>
      sendTalentInvitesDao(payload),
    );
  };

  const createTalentPalylist = () => {
    return useMutation<{}, AxiosError<IErrorResponse>, ITalentPlaylistPayload>((payload: ITalentPlaylistPayload) =>
      createTalentPalylistDao(payload),
    );
  };

  const getTalentCalendarEvents = (
    payload: ITalentCalendarEventsPayload,
    query?: string,
    options?: Omit<
      UseInfiniteQueryOptions<
        ITalentCalendarEventsResponse,
        IAxiosErrorResponse,
        ITalentCalendarEventsResponse,
        QueryKey
      >,
      'queryKey' | 'queryFn'
    >,
  ) => {
    return useInfiniteQuery<ITalentCalendarEventsResponse, IAxiosErrorResponse, ITalentCalendarEventsResponse>(
      ['talent_calendar_events', payload],
      ({ pageParam = '' }) => getTalentCalendarEventsDao({ ...payload, after: pageParam }, query || ''),
      {
        getNextPageParam: (lastPage) => {
          return lastPage.data.calendar_events_connection.pageInfo.hasNextPage
            ? lastPage.data.calendar_events_connection.pageInfo.endCursor
            : undefined;
        },
        enabled: options?.enabled,
      },
    );
  };

  const createCalendarEvent = () => {
    return useMutation<{}, AxiosError<IErrorResponse>, ITalentCalendarEventPayload>(
      (payload: ITalentCalendarEventPayload) => createCalendarEventDao(payload),
    );
  };

  const updateCalendarEvent = () => {
    return useMutation<{}, AxiosError<IErrorResponse>, ITalentCalendarEventPayload>(
      (payload: ITalentCalendarEventPayload) => updateCalendarEventDao(payload),
    );
  };

  const getCalendarAggregates = (
    payload: ITalentCalendarEventAggregatesPayload,
    options?: Omit<
      UseInfiniteQueryOptions<
        ITalentCalendarEventAggregatesResponse,
        IAxiosErrorResponse,
        ITalentCalendarEventAggregatesResponse,
        QueryKey
      >,
      'queryKey' | 'queryFn'
    >,
  ) => {
    return useQuery<ITalentCalendarEventAggregatesResponse>(
      ['calendar_aggregate', payload],
      () => getCalendarAggregatesDao(payload),
      {
        enabled: options?.enabled,
      },
    );
  };

  const deleteCalendarEvent = () => {
    return useMutation<ICommonResponsePayload, IAxiosErrorResponse, { id: string; talentId: string }>(
      ({ id, talentId }) => deleteCalendarEventDao(id, talentId),
    );
  };

  return {
    createTalentPalylist,
    getTalentPlaylist,
    claimGhostedProfile,
    attachTalentToGuardian,
    searchTalentListing,
    getSenderDetails,
    updateMe,
    createTalent,
    updateTalent,
    getMediaTags,
    getTalentProfiles,
    getResume,
    getStatistics,
    searchTalent,
    specificTalent,
    getSkills,
    getGeneral,
    getMe,
    getBiography,
    getSizeCard,
    updateAgencyTalent,
    getTalentList,
    getTotalTalent,
    sendTalentInvites,
    specificTalentByEmail,
    specificTalentGeneralAppearance,
    specificTalentSizeCard,
    specificTalentWorksheetSizeCard,
    specificTalentResume,
    specificTalentBiography,
    specificTalentSkills,
    specificTalentSocial,
    specificTalentProfessions,
    getTalentCalendarEvents,
    createCalendarEvent,
    updateCalendarEvent,
    getCalendarAggregates,
    deleteCalendarEvent,
  };
};
